import { CAMPAIGN_STATES } from "../../../../constants/campaign.constants"
import { setStateType } from "../../../../types/setStateType"
import { campaignData } from "../../store/types/sponsoredProducts"
import { IPlacementDataTable } from "../../types/placement"
import { IPlacementDataForUpdate } from "../../store/types/placements"

interface ISelectCampaignIfNeeded {
  campaignId: number
  placements: IPlacementDataTable[]
  campaignData: campaignData
  setSelectedCampaignIds: setStateType<number[]>
}

export function selectCampaignIfNeeded({
  campaignId,
  placements,
  campaignData,
  setSelectedCampaignIds,
}: ISelectCampaignIfNeeded) {
  const findedCampaign = campaignData.campaigns.find(
    (campaign) => campaign.campaignId === campaignId
  )
  if (!findedCampaign || findedCampaign.campaignStatus === CAMPAIGN_STATES.PAUSED) {
    return
  }

  const hasChanges = hasPlacementChanges(campaignId, placements, campaignData)

  if (hasChanges) {
    setSelectedCampaignIds((prev) => {
      if (prev.includes(campaignId)) return prev
      return [...prev, campaignId]
    })
  }
}

function hasPlacementChanges(
  campaignId: number,
  placements: IPlacementDataTable[],
  campaignData: campaignData
): boolean {
  return placements.some((p) => {
    const newBidAdjustment =
      p.newBidAdjustment !== undefined ? p.newBidAdjustment : p.newbidAdjustment || 0

    const isChanged =
      p.placementModifier !== newBidAdjustment ||
      Number(p.targetCPCAmazon?.toFixed(2) || 0) !== Number(p.targetCPC?.toFixed(2) || 0)

    const isAllowedByFilter =
      !campaignData.excludeAdjustedCampaign ||
      !campaignData.updatedCompanyIdsToday.includes(String(campaignId))
    return isChanged && isAllowedByFilter
  })
}

export interface ISelectionStats {
  enabledLoaded: number
  eligibleChanged: number
  selected: number
}

interface ISelectAllChangedCampaigns {
  campaignData: campaignData
  placementDataForUpdate: IPlacementDataForUpdate[]
  minLimitedCPCByCampaignIdMap: Record<string, number>
}

// Apply on Amazon: every loaded ENABLED campaign whose computed keyword bid or any placement
// modifier differs from Amazon, independent of which rows the user expanded or ticked.
export function selectAllChangedCampaigns({
  campaignData,
  placementDataForUpdate,
  minLimitedCPCByCampaignIdMap,
}: ISelectAllChangedCampaigns): { selectedCampaignIds: number[]; selectionStats: ISelectionStats } {
  const placementsByCampaignId = new Map(
    placementDataForUpdate.map((data) => [Number(data.campaignId), data.placements])
  )
  const enabledCampaigns = campaignData.campaigns.filter(
    (campaign) => campaign.campaignStatus === CAMPAIGN_STATES.ENABLED
  )
  const selected = new Set<number>()

  for (const campaign of enabledCampaigns) {
    const placements = placementsByCampaignId.get(Number(campaign.campaignId))
    if (!placements?.length) continue

    const newKeywordBid = minLimitedCPCByCampaignIdMap[String(campaign.campaignId)]
    const isAllowedByFilter =
      !campaignData.excludeAdjustedCampaign ||
      !campaignData.updatedCompanyIdsToday.includes(String(campaign.campaignId))
    const isKeywordBidChanged =
      newKeywordBid !== undefined &&
      Number(newKeywordBid.toFixed(2)) !== Number((campaign.keywordBid || 0).toFixed(2))

    if (
      (isKeywordBidChanged && isAllowedByFilter) ||
      hasPlacementChanges(campaign.campaignId, placements, campaignData)
    ) {
      selected.add(campaign.campaignId)
    }
  }

  const selectedCampaignIds = [...selected]
  return {
    selectedCampaignIds,
    selectionStats: {
      enabledLoaded: enabledCampaigns.length,
      eligibleChanged: selected.size,
      selected: selectedCampaignIds.length,
    },
  }
}
